import * as Dialog from '@radix-ui/react-dialog';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { SlideOutContent } from '~/chords/SlideOut/components/SlideOutContent';

import { SlideOutBody } from './components/SlideOutBody';
import { SlideOutClose } from './components/SlideOutClose';
import { SlideOutFooter } from './components/SlideOutFooter';
import { SlideOutHeader } from './components/SlideOutHeader';
import {
  ANIMATION_DURATION,
  CONTENT_CLASS,
  DEFAULT_CONTENT_WIDTH,
  OVERLAY_CLASS,
} from './constants';
import type { SlideOutProps, SlideOutRef, SlideOutStatus } from './types';

export const SlideOutComponent = forwardRef<SlideOutRef, SlideOutProps>(
  (
    { children, panelWidth = DEFAULT_CONTENT_WIDTH, onStatusChange, ...props },
    ref,
  ) => {
    const overlayRef = useRef<HTMLDivElement | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const [contentElement, setContentElement] = useState<HTMLDivElement | null>(
      null,
    );
    const [isOpen, setIsOpen] = useState(false);
    const [status, setStatus] = useState<SlideOutStatus>('isClosed');
    const statusRef = useRef<SlideOutStatus>(status);
    const openResolversRef = useRef<(() => void)[]>([]);
    const closeResolversRef = useRef<(() => void)[]>([]);

    statusRef.current = status;

    // Propagate internal lifecycle (isOpening/isOpen/isClosing/isClosed) to consumers.
    useEffect(() => {
      onStatusChange?.(status);
    }, [status, onStatusChange]);

    // Store the panel element for imperative updates and re-run layout when it mounts.
    const setContentRef = useCallback((element: HTMLDivElement | null) => {
      contentRef.current = element;
      setContentElement(element);
    }, []);

    // After content mounts, apply the enter animation and then mark the panel fully open.
    useLayoutEffect(() => {
      if (!contentElement) {
        return;
      }

      let timeoutId: number | undefined;

      const animationId = requestAnimationFrame(() => {
        contentElement.classList.add(CONTENT_CLASS.show);
        contentElement.style.setProperty('transform', '');

        timeoutId = window.setTimeout(() => {
          setStatus((prevStatus) =>
            prevStatus === 'isOpening' ? 'isOpen' : prevStatus,
          );
        }, ANIMATION_DURATION);
      });

      return () => {
        cancelAnimationFrame(animationId);

        if (timeoutId !== undefined) {
          clearTimeout(timeoutId);
        }
      };
    }, [contentElement]);

    // When open completes, flush any promises waiting on `handleOpen` (e.g. duplicate open calls).
    useEffect(() => {
      if (status !== 'isOpen') {
        return;
      }

      const resolvers = openResolversRef.current;

      openResolversRef.current = [];
      resolvers.forEach((resolve) => resolve());
    }, [status]);

    // If the panel closes while still opening, flush pending `open()` promises so they cannot hang.
    useEffect(() => {
      if (status !== 'isClosing' && status !== 'isClosed') {
        return;
      }

      const resolvers = openResolversRef.current;

      if (resolvers.length === 0) {
        return;
      }

      openResolversRef.current = [];
      resolvers.forEach((resolve) => resolve());
    }, [status]);

    // When fully closed, flush any promises waiting on `handleClose`.
    useEffect(() => {
      if (status !== 'isClosed') {
        return;
      }

      const resolvers = closeResolversRef.current;

      closeResolversRef.current = [];
      resolvers.forEach((resolve) => resolve());
    }, [status]);

    // Sync Radix `open` and internal status to closed.
    const closeSlideOut = useCallback(() => {
      setStatus('isClosed');
      setIsOpen(false);
    }, [setIsOpen]);

    // Open the panel: no-op if already open, queue on `isOpening`, otherwise start open flow
    // and await `isOpen` (or resolve early if closed while opening).
    const handleOpen = useCallback(async () => {
      if (statusRef.current === 'isOpen') {
        return;
      }

      const awaitOpenCompletion = () =>
        new Promise<void>((resolve) => {
          openResolversRef.current.push(resolve);
        });

      if (statusRef.current === 'isOpening') {
        await awaitOpenCompletion();

        return;
      }

      setStatus((prevStatus) => {
        if (['isOpen', 'isOpening'].includes(prevStatus)) {
          return prevStatus;
        }

        return 'isOpening';
      });

      setIsOpen(true);

      await awaitOpenCompletion();
    }, [setIsOpen]);

    // Close the panel: no-op if closed, queue on `isClosing`, otherwise run exit animation and await completion.
    const handleClose = useCallback(async () => {
      if (statusRef.current === 'isClosed') {
        return;
      }

      if (statusRef.current === 'isClosing') {
        await new Promise<void>((resolve) => {
          closeResolversRef.current.push(resolve);
        });

        return;
      }

      setStatus('isClosing');

      overlayRef.current?.classList.remove(OVERLAY_CLASS.show);
      contentRef.current?.classList.remove(CONTENT_CLASS.show);

      // There's a bug in Firefox where the animationend event doesn't fire if the
      // animation class is removed and then added back in the same frame. The effect
      // is that the elements are left on top of everything else and the
      // user can't interact with anything. See WMG1-1084 for more details.
      //
      // This is a hack to force a reflow/layout flush of the browser to commit
      // the removal of the old animation class and then treat the new class as
      // a fresh animation. Reference:
      // https://medium.com/@khalidoghli7/how-to-trigger-a-reflow-for-flushing-css-changes-e89c96030c04
      void contentRef.current?.offsetWidth;

      overlayRef.current?.addEventListener('animationend', closeSlideOut, {
        once: true,
      });

      requestAnimationFrame(() => {
        overlayRef.current?.classList.add(OVERLAY_CLASS.hide);

        if (contentRef.current) {
          contentRef.current.classList.add(CONTENT_CLASS.disable);
          contentRef.current.style.transform = `translateX(${panelWidth})`;
        }
      });

      await new Promise<void>((resolve) => {
        closeResolversRef.current.push(resolve);
      });
    }, [panelWidth, closeSlideOut]);

    // On unmount while closed, remove `closeSlideOut` from the overlay element so it cannot run after teardown.
    useEffect(() => {
      const overlay = overlayRef.current;

      return () => {
        if (!isOpen) {
          overlay?.removeEventListener('animationend', closeSlideOut);
        }
      };
    }, [contentRef, isOpen, closeSlideOut]);

    // Expose imperative open/close functions to control the SlideOut from outside.
    useImperativeHandle(
      ref,
      () => ({
        close: handleClose,
        open: handleOpen,
      }),
      [handleClose, handleOpen],
    );

    // Map Radix Dialog to our handleOpen/handleClose functions.
    const handleOpenChange = useCallback(
      (open: boolean) => (open ? handleOpen() : handleClose()),
      [handleOpen, handleClose],
    );

    return (
      <Dialog.Root {...props} open={isOpen} onOpenChange={handleOpenChange}>
        <SlideOutContent
          overlayRef={overlayRef}
          contentRef={setContentRef}
          panelWidth={panelWidth}
        >
          {children}
        </SlideOutContent>
      </Dialog.Root>
    );
  },
);

SlideOutComponent.displayName = 'SlideOut';

export const SlideOut = Object.assign(SlideOutComponent, {
  Header: SlideOutHeader,
  Body: SlideOutBody,
  Footer: SlideOutFooter,
  Close: SlideOutClose,
});
